import * as React from 'react';
import PropTypes from 'prop-types';
import moment from 'moment';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  MenuItem,
  List,
  ListItem,
  ListItemText,
  Typography,
} from '@material-ui/core';
import { withStyles } from '@material-ui/core/styles';
import { AssessmentTypes } from '../../Assessments/+store/assessments/assessments.types';
import { getSchoolYearsModel } from '../../common/+store/utils';

const styles = () => ({
  yearField: {
    marginBottom: 12,
    minWidth: 220,
  },
  list: {
    maxHeight: 360,
    overflowY: 'auto',
    padding: 0,
  },
  empty: {
    padding: '12px 16px',
    opacity: 0.7,
  },
});

const INTERIM_TYPES = [AssessmentTypes.Interim, AssessmentTypes.ComboAssessment, AssessmentTypes.DMACInterim];

// Interim sets whose administration date falls in the school year (Jul 1 to
// Jul 1), the same window the assessment group picker uses.
export const interimSetsForYear = (assessmentList, schoolYear) => {
  const start = moment(`${schoolYear - 1}-07-01`);
  const end = moment(`${schoolYear}-07-01`);
  return (assessmentList || [])
    .filter((a) => INTERIM_TYPES.includes(a.type) && a.date && !a.hiddenFromUser)
    .filter((a) => {
      const date = moment(a.date);
      return date.isValid() && date.isSameOrAfter(start) && date.isBefore(end);
    })
    .sort((a, b) => moment(a.date).valueOf() - moment(b.date).valueOf());
};

// Picks what the matrix rows come from: the report's own prior-year STAAR
// (default) or any interim set, this year or another. Year first so a district
// with years of CBAs is never scrolling one long list.
class GrowthBaselineDialog extends React.PureComponent {
  static propTypes = {
    classes: PropTypes.object.isRequired,
    open: PropTypes.bool.isRequired,
    onClose: PropTypes.func.isRequired,
    onSelect: PropTypes.func.isRequired,
    assessmentList: PropTypes.array,
    reportSchoolYear: PropTypes.number,
    currentAssessmentIds: PropTypes.array,
    selectedBaseline: PropTypes.object,
  };

  state = { year: null };

  getYear = () => this.state.year || this.props.reportSchoolYear || Number(new Date().getFullYear());

  handleYearChange = (event) => this.setState({ year: Number(event.target.value) });

  handlePick = (set) => {
    const { onSelect } = this.props;
    onSelect({
      assessmentId: set.id,
      schoolYear: this.getYear(),
      name: set.name,
      date: set.date,
    });
  };

  render() {
    const {
      classes,
      open,
      onClose,
      onSelect,
      assessmentList,
      reportSchoolYear,
      currentAssessmentIds,
      selectedBaseline,
    } = this.props;
    const year = this.getYear();
    const years = getSchoolYearsModel();
    const sets = interimSetsForYear(assessmentList, year);
    const selectedId = selectedBaseline && selectedBaseline.assessmentId;
    return (
      <Dialog open={open} onClose={onClose} maxWidth="xs" fullWidth>
        <DialogTitle>Compare growth from</DialogTitle>
        <DialogContent>
          <Typography variant="body2" gutterBottom>
            Rows of the growth table come from the assessment chosen here. Columns stay on the report&apos;s current
            assessment.
          </Typography>
          <List className={classes.list}>
            <ListItem button selected={!selectedId} onClick={() => onSelect(null)}>
              <ListItemText primary="Prior Year STAAR" secondary="Default — how the A-F report pairs students" />
            </ListItem>
          </List>
          <TextField
            select
            label="School Year"
            value={String(year)}
            onChange={this.handleYearChange}
            className={classes.yearField}
            margin="dense"
          >
            {Object.entries(years)
              .sort((a, b) => Number(b[0]) - Number(a[0]))
              .map(([value, label]) => (
                <MenuItem key={value} value={value}>
                  {label}
                  {Number(value) === reportSchoolYear ? ' (report year)' : ''}
                </MenuItem>
              ))}
          </TextField>
          <List className={classes.list}>
            {sets.length === 0 && (
              <Typography variant="body2" className={classes.empty}>
                No interim assessments uploaded for {years[year] || year}.
              </Typography>
            )}
            {sets.map((set) => {
              // A set is only pointless as rows when it IS the columns (a
              // one-set report). A member of a multi-set group is fair game:
              // BOY rows against a "most recent" Benchmarks group is the
              // BOY -> EOY comparison districts ask for.
              const currentIds = currentAssessmentIds || [];
              const isColumns = currentIds.length === 1 && currentIds[0] === set.id;
              const inGroup = !isColumns && currentIds.includes(set.id);
              let note = '';
              if (isColumns) note = ' · current columns';
              else if (inGroup) note = " · in this report's group";
              return (
                <ListItem
                  key={set.id}
                  button
                  disabled={isColumns}
                  selected={selectedId === set.id}
                  onClick={() => this.handlePick(set)}
                >
                  <ListItemText primary={set.name} secondary={`${moment(set.date).format('MMM D, YYYY')}${note}`} />
                </ListItem>
              );
            })}
          </List>
        </DialogContent>
        <DialogActions>
          <Button onClick={onClose} color="primary">
            Cancel
          </Button>
        </DialogActions>
      </Dialog>
    );
  }
}

export default withStyles(styles)(GrowthBaselineDialog);
